import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../api/ApiRoutes";
import { buildCasinoKpiMoneyTypeParam } from "../casino-kpis/casinoKpiFilters";

function getAuthenticatedInstance() {
  let token = null;
  try {
    token = JSON.parse(localStorage.getItem("token"));
  } catch {
    token = null;
  }
  return createPrivateAdminPanelInstance(token?.AccessToken);
}

function omitEmptyParams(params) {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v != null && v !== ""),
  );
}

/**
 * API sort param for GET /casino/analytics/games (kept in sync with
 * `CASINO_GAMES_SORT_FIELD_MAP` in `casinoGamesColumns.tsx`).
 */
const SORT_FIELD_TO_API = {
  name: "name",
  providerName: "subprovider",
  category: "category",
  uniquePlayers: "unique_players",
  rounds: "rounds",
  avgRounds: "avg_rounds",
  turnover: "turnover",
  wins: "wins",
  rtpPct: "rtp",
  ggr: "ggr",
  avgBet: "avg_bet",
};

/**
 * @param {string} field UI column key
 * @param {string | null | undefined} order
 * @returns {string | undefined} e.g. `-turnover`
 */
export function buildCasinoGamesSortParam(field, order) {
  if (!field || !order) {
    return undefined;
  }
  const apiField = SORT_FIELD_TO_API[field];
  if (!apiField) {
    return undefined;
  }
  return order === "descend" ? `-${apiField}` : apiField;
}

/**
 * @param {Record<string, unknown>} raw
 * @param {number} [page=1]
 * @param {number} [index=0]
 * @returns {Object | null}
 */
export function normalizeCasinoGamesItem(raw, page = 1, index = 0) {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const name = String(raw.name ?? "");
  const safe = name.length > 0 ? name : "row";
  const url = raw.provider_icon_url;
  return {
    key: `game-${page}-${index}-${safe}`,
    name,
    providerName: String(raw.subprovider ?? ""),
    providerLogoUrl:
      url == null || url === "" ? null : String(url),
    category: String(raw.category ?? ""),
    uniquePlayers: Number(raw.unique_players) || 0,
    rounds: Number(raw.rounds) || 0,
    avgRounds: Number(raw.avg_rounds) || 0,
    turnover: Number(raw.turnover) || 0,
    wins: Number(raw.wins) || 0,
    rtpPct: Number(raw.rtp) || 0,
    ggr: Number(raw.ggr) || 0,
    avgBet: Number(raw.avg_bet) || 0,
  };
}

function pickCasinoGamesEmptyText(body, inner, total) {
  if (total > 0) {
    return null;
  }
  if (inner && typeof inner === "object" && "message" in inner) {
    const m = inner.message;
    if (typeof m === "string" && m.trim().length > 0) {
      return m.trim();
    }
  }
  if (body && typeof body === "object" && body.meta && typeof body.meta === "object") {
    const m = body.meta;
    if (typeof m.message === "string" && m.message.trim().length > 0) {
      return m.message.trim();
    }
  }
  return null;
}

/**
 * @param {unknown} body
 */
export function normalizeCasinoGamesResponse(body) {
  if (!body || typeof body !== "object") {
    return {
      rows: [],
      total: 0,
      page: 1,
      size: 0,
      pages: 0,
      meta: null,
      emptyText: null,
    };
  }
  const inner =
    "data" in body && body.data && typeof body.data === "object"
      ? body.data
      : body;
  if (
    !inner ||
    typeof inner !== "object" ||
    !("items" in inner) ||
    !Array.isArray(inner.items)
  ) {
    return {
      rows: [],
      total: 0,
      page: 1,
      size: 0,
      pages: 0,
      meta: "meta" in body ? body.meta : null,
      emptyText: null,
    };
  }
  const pageNum = "page" in inner ? Number(inner.page) || 1 : 1;
  const items = inner.items;
  const rows = items
    .map((item, i) =>
      normalizeCasinoGamesItem(/** @type {Record<string, unknown>} */ (item), pageNum, i),
    )
    .filter(Boolean);
  const total = "total" in inner ? Number(inner.total) || 0 : 0;
  return {
    rows,
    total,
    page: pageNum,
    size: "size" in inner ? Number(inner.size) || 0 : 0,
    pages: "pages" in inner ? Number(inner.pages) || 0 : 0,
    meta: "meta" in body ? body.meta : null,
    emptyText: pickCasinoGamesEmptyText(body, inner, total),
  };
}

/**
 * @param {Object} [params]
 * @param {string} [params.start_date] YYYY-MM-DD
 * @param {string} [params.end_date] YYYY-MM-DD
 * @param {string} [params.search] game name
 * @param {string} [params.sort] e.g. `-turnover`
 * @param {string} [params.subprovider] subprovider slug (UI "Provider"), omit for all
 * @param {string} [params.category] game/category value, omit for all
 * @param {string[]} [params.money_type] currency types
 */
export async function fetchCasinoGamesReport(params = {}) {
  const start_date = params.start_date;
  const end_date = params.end_date;
  if (!start_date || !end_date) {
    return {
      rows: [],
      total: 0,
      page: 1,
      size: 20,
      pages: 0,
      meta: null,
      emptyText: null,
    };
  }

  const page = Number(params.page) > 0 ? Number(params.page) : 1;
  const size = Number(params.size) > 0 ? Number(params.size) : 20;
  const search =
    typeof params.search === "string" ? params.search.trim() : "";
  const sort =
    typeof params.sort === "string" && params.sort.trim() !== ""
      ? params.sort.trim()
      : undefined;

  const subprovider =
    typeof params.subprovider === "string" && params.subprovider.length > 0
      && params.subprovider !== "all"
      ? params.subprovider
      : undefined;
  const category =
    typeof params.category === "string" && params.category.length > 0
      && params.category !== "all"
      ? params.category
      : undefined;

  const base = omitEmptyParams({
    start_date,
    end_date,
    page,
    size,
    ...(search ? { search } : {}),
    ...(sort ? { sort } : {}),
    ...(subprovider ? { subprovider } : {}),
    ...(category ? { category } : {}),
    ...buildCasinoKpiMoneyTypeParam(params.money_type),
  });

  const instance = getAuthenticatedInstance();
  const { data } = await instance.get(API_ROUTES.GET_CASINO_ANALYTICS_GAMES, {
    params: base,
  });
  return normalizeCasinoGamesResponse(data);
}
